import { userService } from '@libs/data-access/queries'
import { imagesUrls, TopNavbar } from '@libs/ui/ds'
import { useAppNavBarTranslation } from '@libs/utils/translations'
import { useListenWS } from '@spa/bootstrap/AuthenticatedApp/utils/useListenWS'
import { useTrackNavbarHomeInteraction } from '@spa/core/externals/segment/methods/track/hooks/useTrackNavbarHomeInteraction'
import { routes } from '@spa/core/router'
import { useMemoSelector } from '@spa/redux/hooks'
import useHRDashboardPermissions from '@spa/scenes/DashboardRH/hooks/useHRDashboardPermissions'
import { useMemo } from 'react'
import type { FC } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'

import { ComboPayBadge } from './components/ComboPayBadge/ComboPayBadge'
import { NotificationsNavItem } from './components/NotificationsNavItem/NotificationsNavItem'
import { UserMenuNavItem } from './components/UserMenuNavItem/UserMenuNavItem'

export const MainNavbar: FC = () => {
  useListenWS()
  const account = useMemoSelector(getCurrentAccount)
  const navigate = useNavigate()
  const { t } = useAppNavBarTranslation()
  const { trackHomeClicked, trackLogoClicked } = useTrackNavbarHomeInteraction()
  const { enabled_conversation } = account
  const {
    canSeeHRDashboardPage,
    canOnlySeePaidLeaveCounters,
    canSeeToucanGraphs
  } = useHRDashboardPermissions()

  const permissions = userService.membership.useFeaturePermissions()

  const hrDashboardRoute = useMemo(() => {
    if (canOnlySeePaidLeaveCounters) {
      return routes.dashboardRhPaidLeaves.rootPath
    }

    if (canSeeToucanGraphs) {
      return routes.dashboardRhHome.rootPath
    }

    return routes.dashboardRhHome.rootPath
  }, [canOnlySeePaidLeaveCounters, canSeeToucanGraphs])

  const options = useMemo(() => {
    const allOptions = [
      {
        label: t('mainMenu.home'),
        'data-testid': 'navbar-home',
        to: routes.home.rootPath,
        canAccess: true,
        onClick: () => {
          trackHomeClicked()
        }
      },
      {
        label: t('mainMenu.plannings'),
        'data-testid': 'navbar-plannings',
        to: routes.planningWeekly.rootPath,
        canAccess: true
      },
      {
        label: t('mainMenu.team'),
        'data-testid': 'navbar-members',
        to: routes.members.rootPath,
        canAccess: true
      },
      {
        label: t('mainMenu.reports'),
        'data-testid': 'navbar-reports',
        to: routes.reportsPayrollsList.rootPath,
        canAccess: permissions?.payroll.can_access
      },
      {
        label: t('mainMenu.hr'),
        'data-testid': 'navbar-dashboard-rh',
        to: hrDashboardRoute,
        canAccess: canSeeHRDashboardPage
      },
      {
        label: t('mainMenu.articles'),
        'data-testid': 'navbar-articles',
        to: routes.articles.rootPath,
        canAccess: true
      },
      {
        label: t('mainMenu.messages'),
        'data-testid': 'navbar-conversations',
        to: routes.conversations.rootPath,
        canAccess: enabled_conversation
      }
    ]

    return allOptions.filter(option => option.canAccess)
  }, [
    enabled_conversation,
    canSeeHRDashboardPage,
    permissions?.payroll.can_access,
    hrDashboardRoute,
    t,
    trackHomeClicked
  ])

  return (
    <TopNavbar showDropdownBelowBreakpoint='lg' className='sticky top-0 z-50'>
      <TopNavbar.Left>
        <TopNavbar.DropdownItem
          options={options.map(option => ({
            ...option,
            onClick: () => {
              option.onClick?.()
              navigate(option.to)
            }
          }))}
          data-testid='top-navbar-dropdown-item'
        />
        <NavLink tabIndex={-1} to={routes.welcome.rootPath}>
          {({ isActive }) => (
            <TopNavbar.Logo
              imageUrl={imagesUrls.logo.company.comboLogoWhite}
              alt='combo-logo'
              onClick={() => {
                trackLogoClicked()
              }}
              active={isActive}
            />
          )}
        </NavLink>
      </TopNavbar.Left>
      <TopNavbar.Middle>
        {options.map(option => (
          <NavLink tabIndex={-1} key={option.label} to={option.to}>
            {({ isActive }) => (
              <TopNavbar.Item key={option.label} {...option} active={isActive}>
                {option.label}
              </TopNavbar.Item>
            )}
          </NavLink>
        ))}
      </TopNavbar.Middle>
      <TopNavbar.Right className='shrink'>
        <ComboPayBadge account={account} />
        <NotificationsNavItem />
        <UserMenuNavItem />
      </TopNavbar.Right>
    </TopNavbar>
  )
}
